import { minBy } from "lodash";

import { ConsumptionGranularity } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";

import { DATE_FORMAT } from "~/constants/date.constant";
import type { DayRange } from "~/helpers/day-range.helper";
import { m } from "~/paraglide/messages";

import {
  DEFAULT_GRANULARITY,
  DEFAULT_MAX_BUCKETS,
  GRANULARITIES,
  GRANULARITY_ORDER,
  MIN_BUCKETS,
  PERIOD_PRESETS,
  SPAN_COUNTS,
} from "../consumption.constant";
import type { CalendarSpan, PeriodPreset } from "../types/consumption.type";

const WRONG_WAY_PENALTY = GRANULARITY_ORDER.length;

const finerGranularities = (granularity: ConsumptionGranularity): ConsumptionGranularity[] =>
  GRANULARITY_ORDER.slice(0, GRANULARITY_ORDER.indexOf(granularity));

const spanRange = ({ amount, unit }: CalendarSpan, today: string): DayRange => ({
  from: dayjs(today)
    .subtract(amount - 1, unit)
    .startOf(unit)
    .format(DATE_FORMAT),
  to: today,
});

const bucketCount = ({ from, to }: DayRange, granularity: ConsumptionGranularity): number => {
  const { unit } = GRANULARITIES[granularity];
  return dayjs.utc(to).endOf("day").startOf(unit).diff(dayjs.utc(from).startOf(unit), unit) + 1;
};

const oldestReadableDay = (granularity: ConsumptionGranularity, today: string): string => {
  const { unit, retention } = GRANULARITIES[granularity];
  return dayjs(today).subtract(retention.amount, retention.unit).startOf(unit).format(DATE_FORMAT);
};

const exceedsMaxRange = ({ from, to }: DayRange, granularity: ConsumptionGranularity): boolean => {
  const { maxRange } = GRANULARITIES[granularity];
  return !dayjs(to).isBefore(dayjs(from).add(maxRange.amount, maxRange.unit));
};

const isReadable = (range: DayRange, granularity: ConsumptionGranularity, today: string): boolean =>
  range.from >= oldestReadableDay(granularity, today) && !exceedsMaxRange(range, granularity);

const availableGranularities = (range: DayRange, today: string): ConsumptionGranularity[] =>
  GRANULARITY_ORDER.filter(
    granularity => bucketCount(range, granularity) >= MIN_BUCKETS && isReadable(range, granularity, today),
  );

const pickGranularity = (
  requested: ConsumptionGranularity | undefined,
  range: DayRange,
  granularities: ConsumptionGranularity[],
  preset?: PeriodPreset,
): ConsumptionGranularity =>
  [requested, preset ? PERIOD_PRESETS[preset].granularity : DEFAULT_GRANULARITY].find(
    granularity => granularity && granularities.includes(granularity),
  ) ??
  granularities.find(granularity => bucketCount(range, granularity) <= DEFAULT_MAX_BUCKETS) ??
  granularities.at(-1) ??
  ConsumptionGranularity.YEAR;

const unavailableReason = (
  range: DayRange,
  granularity: ConsumptionGranularity,
  granularities: ConsumptionGranularity[],
  today: string,
): string => {
  const tooLong = exceedsMaxRange(range, granularity);
  const tooOld = range.from < oldestReadableDay(granularity, today);
  const tooShort = bucketCount(range, granularity) < MIN_BUCKETS;
  const towardCoarser = (tooLong || tooOld) && !tooShort;
  const towardFiner = tooShort && !tooLong && !tooOld;
  const position = GRANULARITY_ORDER.indexOf(granularity);
  const suggested = minBy(granularities, candidate => {
    const offset = GRANULARITY_ORDER.indexOf(candidate) - position;
    const wrongWay = (towardCoarser && offset < 0) || (towardFiner && offset > 0);
    return Math.abs(offset) + (wrongWay ? WRONG_WAY_PENALTY : 0);
  });
  const view = GRANULARITIES[suggested ?? DEFAULT_GRANULARITY].label();
  const { count, maxRange, retention } = GRANULARITIES[granularity];
  const spanLabel = ({ amount, unit }: CalendarSpan) => SPAN_COUNTS[unit](amount);
  if (tooLong) return m.consumption_view_too_long({ max: spanLabel(maxRange), view });
  if (tooOld) return m.consumption_view_too_old({ retention: spanLabel(retention), view });
  if (tooShort) return m.consumption_view_too_short({ min: count(MIN_BUCKETS), view });
  return m.consumption_view_incompatible({ view });
};

const shiftRange = ({ from, to }: DayRange, { amount, unit }: CalendarSpan): DayRange => ({
  from: dayjs(from).add(amount, unit).format(DATE_FORMAT),
  to: dayjs(to).add(amount, unit).format(DATE_FORMAT),
});

// The wall-clock time is shifted in UTC so the browser's own daylight-saving changes stay out.
const wallClock = (start: string, timezone: string, shift?: CalendarSpan) => {
  const time = dayjs.utc(dayjs(start).tz(timezone).format("YYYY-MM-DDTHH:mm"));
  return shift ? time.add(shift.amount, shift.unit) : time;
};

const bucketSlot = (start: string, granularity: ConsumptionGranularity, timezone: string, shift?: CalendarSpan) =>
  wallClock(start, timezone, shift).format(GRANULARITIES[granularity].slotFormat);

const formatBucket = (start: string, format: string, timezone: string, shift?: CalendarSpan): string =>
  wallClock(start, timezone, shift).format(format);

export {
  availableGranularities,
  bucketCount,
  bucketSlot,
  finerGranularities,
  formatBucket,
  isReadable,
  pickGranularity,
  spanRange,
  shiftRange,
  unavailableReason,
};
